import { useState } from 'react'
import { download, query } from '../../api/client'
import { useQuery } from '../../api/hooks'
import type { ClientFundsControl } from '../../api/types'
import { useAuth } from '../../auth/AuthContext'
import { P } from '../../auth/permissions'
import { Empty, ErrorBanner, Loading, Money, Panel, isoDate, money } from '../../components/ui'

/**
 * Custody + settlements held versus cash actually held (BUILD_PLAN §3.2, §10.2).
 *
 * In an office holding client money this is the highest-value report in the system: it turns an
 * invisible professional-liability risk into a one-line daily check. A negative variance means the
 * office is spending money that is not its own.
 */
export function ClientFundsPage() {
  const { can } = useAuth()
  const [asOf, setAsOf] = useState(isoDate(new Date()))

  const report = useQuery<ClientFundsControl>(`/reports/client-funds${query({ asOf })}`, [asOf])

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>As at</label>
          <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </div>
        <div className="spacer" />
        {can(P.reports.export) && (
          <button
            onClick={() =>
              void download(`/reports/client-funds/excel${query({ asOf })}`, 'client-funds-control.xlsx')
            }
          >
            Export to Excel
          </button>
        )}
      </div>

      <ErrorBanner error={report.error} />
      {report.loading && <Loading what="Checking client funds" />}

      {report.data && (
        <>
          {!report.data.isHealthy && (
            <div className="banner error">
              <div>
                <strong>Client money held is less than the obligation to clients.</strong> The
                shortfall is {money(Math.abs(report.data.variance))}. This means client funds have
                been used for office operations. Investigate before doing anything else.
              </div>
            </div>
          )}

          <div className="grid cols-4" style={{ marginBottom: 16 }}>
            <div className="metric">
              <div className="label">Custody held (3ohda)</div>
              <div className="value">{money(report.data.totalCustodyHeld)}</div>
              <div className="hint">Held to pay court fees and disbursements.</div>
            </div>

            <div className="metric">
              <div className="label">Settlements held</div>
              <div className="value">{money(report.data.totalSettlementsHeld)}</div>
              <div className="hint">Collected on behalf of clients.</div>
            </div>

            <div className="metric">
              <div className="label">Cash actually held</div>
              <div className="value">{money(report.data.totalClientCash)}</div>
              <div className="hint">
                Trust accounts {money(report.data.cashInTrustAccounts)} · designated safes{' '}
                {money(report.data.cashInDesignatedSafes)}
              </div>
            </div>

            <div className={report.data.isHealthy ? 'metric good' : 'metric alert'}>
              <div className="label">Variance</div>
              <div className="value">{money(report.data.variance)}</div>
              <div className="hint">
                {report.data.isHealthy ? 'Cash covers the obligation.' : 'Client money is short.'}
              </div>
            </div>
          </div>

          <Panel title="Per client" flush>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>Code</th>
                    <th>Client</th>
                    <th className="num">Fees billed</th>
                    <th className="num">Fees collected</th>
                    <th className="num">Fees outstanding</th>
                    <th className="num">Custody in</th>
                    <th className="num">Custody spent</th>
                    <th className="num">Custody held</th>
                    <th className="num">Settlements in</th>
                    <th className="num">Settlements out</th>
                    <th className="num">Settlements held</th>
                  </tr>
                </thead>
                <tbody>
                  {report.data.clients.length === 0 && (
                    <tr>
                      <td colSpan={11}>
                        <Empty>No client balances as at this date.</Empty>
                      </td>
                    </tr>
                  )}

                  {report.data.clients.map((client) => (
                    <tr key={client.partyId}>
                      <td className="code">{client.partyCode}</td>
                      <td>{client.partyName}</td>
                      <td className="num">
                        <Money value={client.feesBilled} blankZero />
                      </td>
                      <td className="num">
                        <Money value={client.feesCollected} blankZero />
                      </td>
                      <td className="num">
                        <Money value={client.feesOutstanding} blankZero />
                      </td>
                      <td className="num">
                        <Money value={client.custodyReceived} blankZero />
                      </td>
                      <td className="num">
                        <Money value={client.custodySpent} blankZero />
                      </td>
                      <td className="num strong">
                        <Money value={client.custodyHeld} blankZero />
                      </td>
                      <td className="num">
                        <Money value={client.settlementsReceived} blankZero />
                      </td>
                      <td className="num">
                        <Money value={client.settlementsPaidOut} blankZero />
                      </td>
                      <td className="num strong">
                        <Money value={client.settlementsHeld} blankZero />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={7}>Total held for clients</td>
                    <td className="num">
                      <Money value={report.data.totalCustodyHeld} />
                    </td>
                    <td colSpan={2} />
                    <td className="num">
                      <Money value={report.data.totalSettlementsHeld} />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Panel>
        </>
      )}
    </>
  )
}
